import { useEffect, useState, useCallback } from "react";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Users, Zap, CalendarClock, Heart, Mail, Play } from "lucide-react";

const TIER_LABELS = { basic: "Basic", premium: "Premium", vip: "VIP", free: "Basic (legacy)", elite: "VIP (legacy)", sankalpa: "Sankalpa (legacy)" };

function StatTile({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="border border-stone-200 rounded-2xl bg-white p-5" data-testid={testid}>
      <Icon size={18} className="text-amber-700" />
      <div className="font-serif-display text-3xl text-[#1E1B4B] mt-2">{value}</div>
      <div className="text-xs uppercase tracking-widest text-stone-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-stone-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminPanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/overview");
      setData(d);
    } catch { toast.error("Failed to load admin overview"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data: d } = await api.post("/admin/run-weekly-matching");
      toast.success(`Weekly matching complete — ${d.run.recommendations_sent} recommendations, ${d.run.emails_sent} emails`);
      load();
    } catch { toast.error("Weekly matching run failed"); }
    finally { setRunning(false); }
  };

  if (!user?.is_admin) return (
    <div className="min-h-screen bg-[#F9F8F6]"><Nav /><div className="p-10 text-center text-stone-500">Admin access required.</div></div>
  );
  if (!data) return (
    <div className="min-h-screen bg-[#F9F8F6]"><Nav /><div className="p-10 text-center text-stone-400">Loading…</div></div>
  );

  const run = data.last_weekly_run;
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Admin · Automation Panel</div>
            <h1 className="font-serif-display text-4xl text-[#1E1B4B] mt-2">Everything runs itself.</h1>
            <p className="text-sm text-stone-500 mt-2">Weekly matching fires every Monday 9:00 AM IST. Reminders and nudges are fully automated.</p>
          </div>
          <button onClick={runNow} disabled={running} data-testid="run-weekly-matching-btn"
            className="btn-pill inline-flex items-center gap-2 text-sm text-white px-5 py-2.5" style={{ background: "#1E1B4B" }}>
            <Play size={14} /> {running ? "Running…" : "Run Weekly Matching Now"}
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile icon={Users} label="Active users" value={data.total_users} testid="admin-total-users" />
          <StatTile icon={Zap} label="Recs last run" value={run ? run.recommendations_sent : "—"}
            sub={run ? `${run.triggered_by} · week of ${run.week_of}` : "No run yet"} testid="admin-last-run" />
          <StatTile icon={Mail} label="Emails this week" value={data.emails_this_week} testid="admin-emails-week" />
          <StatTile icon={CalendarClock} label="Upcoming consults" value={data.upcoming_consultations.length} testid="admin-consult-count" />
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Users by tier */}
          <div className="border border-stone-200 rounded-2xl bg-white p-6" data-testid="admin-users-by-tier">
            <div className="overline">Users by tier</div>
            <div className="mt-4 space-y-3">
              {Object.entries(data.users_by_tier).map(([tier, count]) => (
                <div key={tier} className="flex items-center justify-between text-sm">
                  <span className="text-stone-600">{TIER_LABELS[tier] || tier}</span>
                  <span className="font-serif-display text-xl text-[#1E1B4B]">{count}</span>
                </div>
              ))}
            </div>
            {run && (
              <div className="mt-5 border-t border-stone-100 pt-4 text-xs text-stone-500">
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Last weekly delivery</div>
                <div>Users processed: <b className="text-[#1E1B4B]">{run.users_processed}</b></div>
                <div>Recommendations: <b className="text-[#1E1B4B]">{run.recommendations_sent}</b></div>
                <div>Emails sent: <b className="text-[#1E1B4B]">{run.emails_sent}</b></div>
                <div className="mt-1">Status: <span className="text-emerald-700 font-medium capitalize">{run.status}</span></div>
              </div>
            )}
          </div>

          {/* Interested this week */}
          <div className="border border-stone-200 rounded-2xl bg-white p-6" data-testid="admin-interested-list">
            <div className="flex items-center gap-2"><Heart size={15} className="text-rose-500" /><div className="overline">Interested this week</div></div>
            {data.interested_this_week.length === 0 ? (
              <div className="mt-4 text-sm text-stone-400">No interest responses yet this week.</div>
            ) : (
              <div className="mt-4 space-y-3">
                {data.interested_this_week.map((r) => (
                  <div key={r.id} className="border border-stone-100 rounded-xl p-3 text-sm">
                    <div className="text-[#1E1B4B] font-medium">{r.user_name} → {r.candidate_name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{r.overall_score}% match · responded {new Date(r.responded_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Consultation calendar */}
          <div className="border border-stone-200 rounded-2xl bg-white p-6" data-testid="admin-consultation-calendar">
            <div className="flex items-center gap-2"><CalendarClock size={15} className="text-amber-700" /><div className="overline">K Singh — Consultation Calendar</div></div>
            {data.upcoming_consultations.length === 0 ? (
              <div className="mt-4 text-sm text-stone-400">No upcoming consultations.</div>
            ) : (
              <div className="mt-4 space-y-3">
                {data.upcoming_consultations.map((c) => (
                  <div key={c.id} className="border border-stone-100 rounded-xl p-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#1E1B4B] font-medium">{c.user_name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">{c.tier === "vip" ? "VIP" : c.tier}</span>
                    </div>
                    <div className="text-xs text-stone-500 mt-1">{c.scheduled_date} · {c.scheduled_time} IST · 30 min</div>
                    <div className="text-xs text-stone-400">{c.user_email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
